<script setup lang="ts">
/**
 * A button: a painted plate from the game kit (ui/kit/kit.css), stretched as
 * a 9-slice so its rim, rivets and gloss keep their size at any width.
 *
 * Four paints, and the money is in the colour: `primary` is signal yellow and
 * does the ordinary thing or spends credits; `gem` is sea green and always
 * spends seals; `danger` is naval red; `quiet` is navy steel with a brass rim.
 * The label is game lettering with an ink outline (yellow carries dark ink).
 *
 * It answers the hand (life-layer.md §2, §3 P0): the plate sinks on press,
 * an ink blot spreads from the touch point, a glyph wiggles on hover, the one
 * call to action (`cta`) nudges its glyph while idle, `error` shakes it,
 * `loading` turns a brass compass needle, and `heldUntil` (a 429) shows a red
 * countdown chip. While loading or held the button is inert but focusable.
 */
import { ref, watch } from 'vue';

import { playCue } from '@/audio/sfx';
import { reducedMotion } from '@/engine/motion';
import { useCooldown } from './cooldown';

/** Ink blot: opacity at the touch, and how long it spreads (life-layer.md §3 P0). */
const BLOT_OPACITY = 0.45;
const BLOT_MS = 420;

const props = withDefaults(
  defineProps<{
    variant?: 'primary' | 'quiet' | 'danger' | 'gem';
    size?: 'sm' | 'md' | 'lg';
    type?: 'button' | 'submit';
    disabled?: boolean;
    /** Shows a spinning compass needle, dims the label and blocks the press. */
    loading?: boolean;
    /** Fills the width of its container. */
    block?: boolean;
    /** The single primary call to action on the screen: its glyph nudges while idle. */
    cta?: boolean;
    /** Set briefly by the caller after a refusal: shakes and draws a danger edge. */
    error?: boolean;
    /** Epoch ms until which the server holds this action (429). Inert, with a countdown chip. */
    heldUntil?: number;
  }>(),
  {
    variant: 'quiet',
    size: 'md',
    type: 'button',
    disabled: false,
    loading: false,
    block: false,
    cta: false,
    error: false,
    heldUntil: undefined,
  },
);

const emit = defineEmits<{ click: [MouseEvent] }>();

const { held, seconds } = useCooldown(() => props.heldUntil);
/** Loading and held keep focus (aria-disabled); `disabled` is a real disabled button. */
const inert = () => props.loading || held.value;

// The press itself is sounded by the document listener (`audio/index.ts`); only a refusal is ours.
watch(
  () => props.error,
  (now) => {
    if (now) playCue('ui.error');
  },
);

const root = ref<HTMLButtonElement | null>(null);
const blot = ref<HTMLSpanElement | null>(null);

function onPointerDown(e: PointerEvent): void {
  const el = root.value;
  const dot = blot.value;
  if (!el || !dot || props.disabled || inert() || reducedMotion() || typeof dot.animate !== 'function') return;
  const r = el.getBoundingClientRect();
  const x = e.clientX - r.left;
  const y = e.clientY - r.top;
  // A circle that reaches the farthest corner covers the whole plate.
  const reach = Math.hypot(Math.max(x, r.width - x), Math.max(y, r.height - y));
  dot.style.width = dot.style.height = `${reach * 2}px`;
  dot.style.left = `${x - reach}px`;
  dot.style.top = `${y - reach}px`;
  dot.getAnimations().forEach((a) => a.cancel());
  dot.animate(
    [
      { transform: 'scale(0)', opacity: BLOT_OPACITY },
      { transform: 'scale(1)', opacity: 0 },
    ],
    { duration: BLOT_MS, easing: 'ease-out' },
  );
}

function onClick(e: MouseEvent): void {
  if (inert()) {
    e.preventDefault();
    return;
  }
  emit('click', e);
}
</script>

<template>
  <button
    ref="root"
    :type="type"
    class="btn"
    :class="[`v-${variant}`, `s-${size}`, { block, loading, held, cta, error }]"
    :disabled="disabled"
    :aria-disabled="loading || held ? 'true' : undefined"
    :aria-busy="loading ? 'true' : undefined"
    @pointerdown="onPointerDown"
    @click="onClick"
  >
    <span ref="blot" class="blot" aria-hidden="true"></span>
    <svg v-if="loading" class="needle" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path class="needle-n" d="M12 2 15.5 12h-7Z" />
      <path class="needle-s" d="M12 22 8.5 12h7Z" />
    </svg>
    <span class="btn-label"><slot /></span>
    <span v-if="held" class="chip" role="timer">{{ seconds }}s</span>
  </button>
</template>

<style scoped>
.btn {
  --h: 3rem;
  position: relative;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45em;
  min-height: var(--h);
  padding: 0 calc(var(--h) * 0.55) calc(var(--h) * 0.06);
  /* The painted plate: rim and bevel from the image, never from CSS (kit.css). */
  border: 0 solid transparent;
  /* 40% rows top and bottom leave a middle band that stretches, so tall plates have no seam under the label. */
  border-image: var(--btn-img) 40% 12% fill / calc(var(--h) * 0.4) calc(var(--h) * 0.42) stretch;
  background: none;
  color: #fff;
  font-family: var(--font-game);
  font-size: calc(var(--h) * 0.38);
  font-weight: 400;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  touch-action: manipulation;
  filter: drop-shadow(0 3px 0 rgba(0, 0, 0, 0.35));
  /* 110 ms: the press (life-layer.md §3 P0). */
  transition:
    filter 110ms var(--ease),
    transform 110ms var(--ease);
}

.btn-label {
  display: inline-flex;
  align-items: center;
  gap: 0.45em;
  line-height: 1;
  -webkit-text-stroke: var(--kit-stroke) var(--btn-ink);
  paint-order: stroke fill;
  text-shadow: 0 0.08em 0 var(--btn-ink);
}

.s-sm {
  --h: 2.5rem;
}

.s-lg {
  --h: 3.75rem;
}

@media (pointer: coarse) {
  .s-sm {
    --h: 2.75rem;
  }
}

.block {
  width: 100%;
}

.btn:not(:disabled):active {
  transform: translateY(3px);
  filter: brightness(0.92) drop-shadow(0 0 0 rgba(0, 0, 0, 0));
}

/* Hover only brightens and lifts where a pointer can hover; nothing depends on it. */
@media (hover: hover) {
  .btn:not(:disabled):not(.loading):not(.held):hover {
    filter: brightness(1.08) drop-shadow(0 4px 0 rgba(0, 0, 0, 0.35));
    transform: translateY(-1px);
  }

  .btn:not(:disabled):not(.loading):not(.held):hover :deep(.glyph),
  .btn:not(:disabled):not(.loading):not(.held):hover :deep(.game-icon) {
    animation: wiggle 0.6s;
  }

  .btn:not(:disabled):not(.loading):not(.held):active {
    transform: translateY(3px);
  }
}

.btn:disabled {
  opacity: 0.5;
  filter: saturate(0.25);
  cursor: not-allowed;
}

.loading,
.held {
  cursor: progress;
}

.held {
  cursor: not-allowed;
  filter: saturate(0.4);
}

.loading .btn-label {
  opacity: 0.55;
}

/* The one call to action: its glyph nudges, slowly, while nothing is happening. */
.cta:not(:disabled):not(.loading):not(.held) :deep(.glyph),
.cta:not(:disabled):not(.loading):not(.held) :deep(.game-icon) {
  animation: idle-nudge 3.2s infinite;
}

.error {
  animation: shake 0.32s;
}

/* The blot is one reused span, sized and placed by script at the touch point. */
.blot {
  position: absolute;
  border-radius: 50%;
  background: #fff;
  opacity: 0;
  pointer-events: none;
}

/* Brass compass needle. */
.needle {
  flex: none;
  width: 1.15em;
  height: 1.15em;
  animation: spin 1.1s linear infinite;
}

.needle-n {
  fill: var(--accent-bright);
  stroke: var(--kit-ink);
  stroke-width: 1;
  stroke-linejoin: round;
}

.needle-s {
  fill: var(--kit-ink);
  opacity: 0.7;
}

/* The 429 chip: a red label stuck on the plate, counting down. */
.chip {
  flex: none;
  padding: 0.25em 0.5em 0.15em;
  border-radius: 6px;
  background: #b0362a;
  color: #fff;
  font-family: var(--font-game-body);
  font-size: 0.75em;
  letter-spacing: 0;
  line-height: 1;
}

.v-quiet {
  --btn-img: url('/art/ui/kit/btn_neutral.webp');
  --btn-ink: var(--kit-ink);
}

/* Yellow paint carries dark lettering, no outline: the outline would muddy it. */
.v-primary {
  --btn-img: url('/art/ui/kit/btn_primary.webp');
  --btn-ink: transparent;
  color: #2a1d02;
}

.v-primary .btn-label {
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.45);
}

.v-danger {
  --btn-img: url('/art/ui/kit/btn_danger.webp');
  --btn-ink: var(--kit-ink);
}

.v-gem {
  --btn-img: url('/art/ui/kit/btn_go.webp');
  --btn-ink: var(--kit-ink);
}
</style>
